import {
  CLIENT_STATUS,
  LEAD_STATUS,
  PROJECT_STATUS,
  QUOTATION_STATUS,
  CONTRACT_STATUS,
  INVOICE_STATUS,
  PAYMENT_STATUS,
  TASK_STATUS,
  APPOINTMENT_STATUS,
  INTERACTION_TYPES,
} from "../types/crmTypes";

export const generateClientCode = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `CLI-${year}-${random}`;
};

export const generateLeadCode = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `LEAD-${year}-${random}`;
};

export const generateProjectCode = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PROJ-${year}-${random}`;
};

export const generateQuotationNumber = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `QTN-${year}-${random}`;
};

export const generateInvoiceNumber = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `INV-${year}-${random}`;
};

export const generateContractNumber = () => {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `CTR-${year}-${random}`;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export const formatCurrency = (amount, currency = "INR") => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const getClientStatusDisplay = (status) => {
  const map = {
    [CLIENT_STATUS.ACTIVE]: { label: "Active", color: "success" },
    [CLIENT_STATUS.INACTIVE]: { label: "Inactive", color: "default" },
    [CLIENT_STATUS.ARCHIVED]: { label: "Archived", color: "warning" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getLeadStatusDisplay = (status) => {
  const map = {
    [LEAD_STATUS.NEW]: { label: "New", color: "info" },
    [LEAD_STATUS.CONTACTED]: { label: "Contacted", color: "default" },
    [LEAD_STATUS.QUALIFIED]: { label: "Qualified", color: "warning" },
    [LEAD_STATUS.PROPOSAL_SENT]: { label: "Proposal Sent", color: "warning" },
    [LEAD_STATUS.WON]: { label: "Won", color: "success" },
    [LEAD_STATUS.LOST]: { label: "Lost", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getLeadPriorityDisplay = (priority) => {
  const map = {
    high: { label: "High", color: "error" },
    medium: { label: "Medium", color: "warning" },
    low: { label: "Low", color: "default" },
  };
  return map[priority] || { label: priority, color: "default" };
};

export const getLeadTemperatureDisplay = (temp) => {
  const map = {
    hot: { label: "🔥 Hot", color: "error" },
    warm: { label: "🌤️ Warm", color: "warning" },
    cold: { label: "❄️ Cold", color: "default" },
  };
  return map[temp] || { label: temp, color: "default" };
};

export const getProjectStatusDisplay = (status) => {
  const map = {
    [PROJECT_STATUS.ENQUIRY]: { label: "Enquiry", color: "info" },
    [PROJECT_STATUS.PLANNING]: { label: "Planning", color: "default" },
    [PROJECT_STATUS.ACTIVE]: { label: "Active", color: "success" },
    [PROJECT_STATUS.COMPLETED]: { label: "Completed", color: "success" },
    [PROJECT_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getQuotationStatusDisplay = (status) => {
  const map = {
    [QUOTATION_STATUS.DRAFT]: { label: "Draft", color: "default" },
    [QUOTATION_STATUS.SENT]: { label: "Sent", color: "info" },
    [QUOTATION_STATUS.VIEWED]: { label: "Viewed", color: "warning" },
    [QUOTATION_STATUS.ACCEPTED]: { label: "Accepted", color: "success" },
    [QUOTATION_STATUS.REJECTED]: { label: "Rejected", color: "error" },
    [QUOTATION_STATUS.EXPIRED]: { label: "Expired", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getContractStatusDisplay = (status) => {
  const map = {
    [CONTRACT_STATUS.DRAFT]: { label: "Draft", color: "default" },
    [CONTRACT_STATUS.SENT]: { label: "Sent", color: "info" },
    [CONTRACT_STATUS.SIGNED]: { label: "Signed", color: "success" },
    [CONTRACT_STATUS.COMPLETED]: { label: "Completed", color: "success" },
    [CONTRACT_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getInvoiceStatusDisplay = (status) => {
  const map = {
    [INVOICE_STATUS.DRAFT]: { label: "Draft", color: "default" },
    [INVOICE_STATUS.SENT]: { label: "Sent", color: "info" },
    [INVOICE_STATUS.VIEWED]: { label: "Viewed", color: "info" },
    [INVOICE_STATUS.PARTIALLY_PAID]: {
      label: "Partially Paid",
      color: "warning",
    },
    [INVOICE_STATUS.PAID]: { label: "Paid", color: "success" },
    [INVOICE_STATUS.OVERDUE]: { label: "Overdue", color: "error" },
    [INVOICE_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getPaymentStatusDisplay = (status) => {
  const map = {
    [PAYMENT_STATUS.PENDING]: { label: "Pending", color: "warning" },
    [PAYMENT_STATUS.COMPLETED]: { label: "Completed", color: "success" },
    [PAYMENT_STATUS.FAILED]: { label: "Failed", color: "error" },
    [PAYMENT_STATUS.REFUNDED]: { label: "Refunded", color: "default" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getTaskStatusDisplay = (status) => {
  const map = {
    [TASK_STATUS.PENDING]: { label: "Pending", color: "default" },
    [TASK_STATUS.IN_PROGRESS]: { label: "In Progress", color: "info" },
    [TASK_STATUS.COMPLETED]: { label: "Completed", color: "success" },
    [TASK_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getAppointmentStatusDisplay = (status) => {
  const map = {
    [APPOINTMENT_STATUS.SCHEDULED]: { label: "Scheduled", color: "info" },
    [APPOINTMENT_STATUS.CONFIRMED]: { label: "Confirmed", color: "success" },
    [APPOINTMENT_STATUS.COMPLETED]: { label: "Completed", color: "success" },
    [APPOINTMENT_STATUS.CANCELLED]: { label: "Cancelled", color: "error" },
    [APPOINTMENT_STATUS.NO_SHOW]: { label: "No Show", color: "error" },
  };
  return map[status] || { label: status, color: "default" };
};

export const getInteractionTypeIcon = (type) => {
  const map = {
    [INTERACTION_TYPES.MEETING]: "📅",
    [INTERACTION_TYPES.CALL]: "📞",
    [INTERACTION_TYPES.EMAIL]: "📧",
    [INTERACTION_TYPES.WHATSAPP]: "💬",
    [INTERACTION_TYPES.SMS]: "💬",
    [INTERACTION_TYPES.WEBSITE_FORM]: "🌐",
    [INTERACTION_TYPES.INSTAGRAM]: "📸",
    [INTERACTION_TYPES.OTHER]: "📝",
  };
  return map[type] || "📝";
};

export const validateClientData = (data) => {
  const errors = {};
  if (!data.firstName?.trim() && !data.fullName?.trim()) {
    errors.fullName = "Client name is required";
  }
  return errors;
};

export const validateLeadData = (data) => {
  const errors = {};
  if (!data.firstName?.trim() && !data.fullName?.trim()) {
    errors.fullName = "Lead name is required";
  }
  return errors;
};

export const validateProjectData = (data) => {
  const errors = {};
  if (!data.title?.trim()) {
    errors.title = "Project title is required";
  }
  if (!data.clientId) {
    errors.clientId = "Client is required";
  }
  return errors;
};

export const calculateDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  const diff = target - today;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};
